import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import BreadCrum from "../../../lib/BreadCrum";
import { useHistory } from "react-router";
import useQuery from "../../../utils/useQuery";
import { useEffect, useState } from "react";
import { RootStateType } from "../../../redux-store/reducers";
import { useSelector, useDispatch } from "react-redux";
import { navigationData as NavigationData, Order, PaginationObj } from "../types";
import BondsOverview from "./bondsOverview";
import BondsHoldingDetails from "./bondsHoldingDetails";
import BondsTransactionDetails from "./bondsTransactionDetails";
import { ChartIcon, CurrencyIcon, MoneyGrow, PercentageIcons, TotalInvestors } from "../../../assets/SvgIcons";
import { BondsData, InfoCardProps } from "./types";
import { formatCurrency, isModuleAllowed } from "../../../utils/utilityFunctions";
import { USER_ROLES } from "../../../utils/constant";
import { getSchemeDetails, totalCardValuesApi } from "../../../redux-store/actions/product";
import { GetSchemeDetailsResponseBody, SchemeDetail, TotalCardValuesRes } from "../../../redux-store/types/products";
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";
import BondsOverViewLoading from "./bondsOverViewLoading";
import HoldingDetailsLoader from "./holdingDetailsLoader";


function InfoCard({
    Icon,
    title,
    value,
    valueTextColor = "text.primary",
    isLoading,
    percentage
}: InfoCardProps) {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const formatedPercentage = !Number.isNaN(Number(percentage ?? 0)) ?
        Math.round((percentage ?? 0) * 100) / 100 : '0'
    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "15px",
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                padding: "25px 18px",
                display: "flex",
                height: '100%',
                alignItems: 'center'
            })}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >

                <Icon />
            </Box>
            <Box
                sx={{
                    flex: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    rowGap: "8px",
                    width: '80%',
                }}
            >
                {isLoading && (
                    <>
                        <Skeleton
                            animation="wave"
                            sx={{ width: '60%' }}
                        />
                        <Skeleton
                            animation="wave"
                            sx={{ width: '100%' }}
                        />
                    </>
                )}
                {!isLoading && (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "14px",
                                color: theme.palette.text.primary,
                                fontWeight: "500",
                            })}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "27.5px",
                                color: valueTextColor
                            }}
                        >
                            {title === "Market Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Invested Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Gain/Loss" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Absolute Return %" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
                            )}
                            {title === "Total Investors" && (
                                value ?? 0
                            )}
                        </Typography>
                    </>
                )}
            </Box>

        </Box>
    )
}

const tabsList = ['Overview', 'Holding Details', 'Transaction Details']

export default function Bonds(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const { pathname: pathName, state } = history.location
    const navState = state as NavigationData
    const query = useQuery()
    const initialRouter = pathName.split('/').filter((item: string) => item !== '')
    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { label: selectedPortfolioLabel = "", value: selectedPortfolioValue = "" } = selectedPortfolio
    const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId = "", investorClientId = "" } = useSelector((store: RootStateType) => store.id);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const clientCode = ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode ?? '' : investorClientId;
    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        ...(!initialRouter.includes('product') ? [{
            name: `${initialRouter[1]} ${selectedPortfolioLabel ? `(${selectedPortfolioLabel})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }] : []),
        {
            name: 'Debt',
            path: !initialRouter.includes('product') ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` : `/${initialRouter[0]}/${initialRouter[1]}}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }
    ] : [])

    const investorRouterPath = [
        {
            name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
        },
        {
            name: 'Debt',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }
    ]

    const initialBondsDataArray = [
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <MoneyGrow {...props} viewBox="0 0 57 50" />,
            title: "Market Value",
            value: 0
        },
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <CurrencyIcon {...props} />,
            title: "Invested Value",
            value: 0
        },
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 50" />,
            title: "Gain/Loss",
            value: 0,
            percentage: 0,
            valueTextColor: 'success.800'
        },
        {
            Icon: initialRouter.includes('product') ? (props: React.SVGProps<SVGSVGElement>) => <TotalInvestors {...props} viewBox="0 0 57 50" /> : (props: React.SVGProps<SVGSVGElement>) => <PercentageIcons {...props} viewBox="0 0 57 50" />,
            title: initialRouter.includes('product') ? "Total Investors" : "Absolute Return %",
            value: 0
        }
    ]

    const [selectedTab, setSelectedTab] = useState(query.get('tab') || 'Overview')
    const [bondsDataObj, setBondsDataObj] = useState<{ isSuccess: boolean; data: BondsData[] }>({ isSuccess: true, data: initialBondsDataArray })
    const [cardValuesLoading, setCardValuesLoading] = useState<boolean>(false);
    const fetchBondsCardData = async () => {
        try {
            setCardValuesLoading(true)
            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: '',
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode ?? "" : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: "DB"
            }

            const { isSuccess, message, result } = await (dispatch(totalCardValuesApi(payload)) as unknown as Promise<TotalCardValuesRes>)

            if (isSuccess) {
                setBondsDataObj(prevState => ({
                    isSuccess: true,
                    data: prevState.data.map((obj: BondsData) => {
                        if (obj.title === "Market Value") {
                            return {
                                ...obj,
                                value: result?.totalMarketValue ?? 0
                            }
                        }
                        if (obj.title === "Invested Value") {
                            return {
                                ...obj,
                                value: result?.totalInvestedValue ?? 0
                            }
                        }
                        if (obj.title === "Gain/Loss") {
                            return {
                                ...obj,
                                value: result?.gainLoss ?? 0,
                                percentage: result?.percentage ?? 0,
                                valueTextColor: (result?.gainLoss ?? 0) >= 0 ? 'success.800' : 'error.500'
                            }
                        }
                        if (obj.title === "Absolute Return %") {
                            return {
                                ...obj,
                                value: result?.percentage ?? 0,
                                valueTextColor: (result?.percentage ?? 0) >= 0 ? 'success.800' : 'error.500'
                            }
                        }

                        if (obj.title === "Total Investors") {
                            return {
                                ...obj,
                                value: result?.totalInvestors ?? 0
                            }
                        }
                        return obj
                    })
                }))
            }
            else {
                setBondsDataObj({
                    isSuccess: false,
                    data: initialBondsDataArray
                })
            }
            return isSuccess;
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setCardValuesLoading(false)
        }
    }


    useEffect(() => {
        fetchBondsCardData();
    }, [])




    const handleTabChange = async (currentTab: string) => {
        setSelectedTab(currentTab)

        history.replace({
            pathname: location.pathname, search: `?tab=${currentTab}`, state: {
                clientCode: navState?.clientCode ?? ""
            }
        })
    }

    const currentInvestedValueObj = bondsDataObj
        .data
        .find(obj => obj.title === "Invested Value");
    const portfolioNotExistStatus = Number(currentInvestedValueObj?.value ?? 0) === 0


    return (
        <Box sx={{ marginTop: "15px" }}>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
            <Grid
                container
                sx={{
                    marginTop: "25px",
                    display: "flex",
                }}
                spacing={2}
            >
                {bondsDataObj.data.map((obj: BondsData) => (
                    <Grid
                        item
                        key={obj.title}
                        xs={12}
                        md={6}
                        lg={3}
                    >
                        <InfoCard
                            Icon={obj.Icon}
                            title={obj.title}
                            value={obj.value}
                            valueTextColor={obj?.valueTextColor}
                            isLoading={cardValuesLoading}
                            percentage={obj?.percentage}
                        />
                    </Grid>
                ))}
            </Grid>
            <Box
                sx={(theme: Theme) => ({
                    display: 'flex',
                    gap: '10px',
                    paddingBottom: '15px',
                    borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                    overflowX: 'auto',
                    marginTop: "50px"
                })}
                role="tablist"
            >
                {tabsList.map((item) => {
                    const tabId = `bonds-tab-${item.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`
                    return (
                        <Button
                            variant={selectedTab === item ? "contained" : "text"}
                            key={item}
                            id={tabId}
                            onClick={() => handleTabChange(item)}
                            sx={(theme: Theme) => ({
                                padding: '5px 15px',
                                background: selectedTab === item ? theme.palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: 'pointer'
                            })}
                            disabled={portfolioNotExistStatus}
                            aria-controls="bonds-tabpanel"
                            aria-selected={selectedTab === item}
                            role="tab"
                        >
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: selectedTab === item ? theme.palette.textDark.primary : theme.palette.grey[700]
                                })}
                            >
                                {item}
                            </Typography>
                        </Button>
                    )
                })}
            </Box>
            <Box
                id="bonds-tabpanel"
                role="tabpanel"
                aria-labelledby={`bonds-tab-${selectedTab.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                sx={{ marginTop: "25px" }}
            >
                {cardValuesLoading && <>
                    {selectedTab === 'Overview' &&
                        <BondsOverViewLoading />
                    }
                    {selectedTab === 'Holding Details' &&
                        <HoldingDetailsLoader
                        />
                    }
                </>
                }
                {(!cardValuesLoading && portfolioNotExistStatus) &&
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment isSuccess={bondsDataObj.isSuccess} />
                    </Box>
                }
                {!cardValuesLoading && !portfolioNotExistStatus &&
                    <>
                        {selectedTab === 'Overview' &&
                            <BondsOverview />
                        }
                        {selectedTab === 'Holding Details' &&
                            <BondsHoldingDetails
                            />
                        }
                        {selectedTab === 'Transaction Details' &&
                            <BondsTransactionDetails
                                clientCode={navState?.clientCode ?? ""} />}
                    </>}
            </Box>
        </Box >
    )
}